// CLI v0.9 — `cloudgrid versions <id|org/slug>` (+ tag / pin / unpin).
//
// ADR 036 V3 (spec 2026-06-07 §4.6). Lists the immutable version snapshots
// minted on each successful plug, and tags / pins / unpins a version.
//
// Resolution mirrors `info` (V2's global-addressing work): an explicit id is
// passed straight through as the `:id` segment (the server resolves a UUID, an
// `org/slug` ref, or a bare slug in the active org); omitting it reads the
// linked entity from `.cloudgrid/link.json`.
//
// With the server-side `entity_versions_enabled` flag off, the list endpoint
// returns an empty list — the verb prints "No versions yet" rather than erroring.

import { Command } from 'commander';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { requireLink } from '../services/link.js';
import { handleV2Error } from '../handle-error.js';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';
import { formatRelativeTime } from './status.js';

/** One version row as returned by the API (subset the CLI renders). */
interface VersionRow {
  version_id: string;
  version_number: number;
  deployed_at: string;
  deployed_by_name: string;
  tag: string | null;
  pinned: boolean;
}

interface VersionsListResponse {
  versions: VersionRow[];
  total: number;
}

/** Explicit id → as-is; omitted → the linked entity's id. */
function resolveEntityId(idArg: string | undefined): string {
  if (idArg !== undefined && idArg.length > 0) return idArg;
  return requireLink().entity_id;
}

function renderList(resp: VersionsListResponse): string {
  if (resp.versions.length === 0) {
    return 'No versions yet. Versions are minted on each successful plug.';
  }
  const lines: string[] = [];
  for (const v of resp.versions) {
    const markers: string[] = [];
    if (v.tag) markers.push(v.tag);
    if (v.pinned) markers.push('pinned');
    const suffix = markers.length > 0 ? `  (${markers.join(', ')})` : '';
    lines.push(
      `  ${v.version_id}  v${v.version_number}  ${formatRelativeTime(v.deployed_at)}  ${v.deployed_by_name}${suffix}`,
    );
  }
  if (resp.total > resp.versions.length) {
    lines.push(`  … ${resp.total - resp.versions.length} more (use --limit to see more)`);
  }
  return lines.join('\n');
}

// ── list ──────────────────────────────────────────────────────────────────

export interface RunVersionsOpts {
  client: IV2Client;
  id?: string;
  limit?: number;
  json?: boolean;
  output?: string;
}

export async function runVersionsList(opts: RunVersionsOpts): Promise<void> {
  const id = resolveEntityId(opts.id);
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const qs: Record<string, string | number | undefined> = {};
  if (typeof opts.limit === 'number') qs.limit = opts.limit;
  const resp = await opts.client.get<VersionsListResponse>(
    `/api/v2/runtimes/${encodeURIComponent(id)}/versions`,
    qs,
  );
  out.json(() => resp);
  out.human(() => renderList(resp));
}

// ── tag / pin / unpin ───────────────────────────────────────────────────────

export async function runVersionsTag(
  client: IV2Client,
  id: string,
  versionId: string,
  tag: string,
  json: boolean,
): Promise<void> {
  const resolved = resolveEntityId(id);
  const out = Output.fromOpts({ json, quiet: false });
  const version = await client.patch<VersionRow>(
    `/api/v2/runtimes/${encodeURIComponent(resolved)}/versions/${encodeURIComponent(versionId)}`,
    { tag },
  );
  out.json(() => version);
  out.human(() =>
    version.tag
      ? `Tagged ${version.version_id} as ${version.tag}.`
      : `Cleared the tag on ${version.version_id}.`,
  );
}

export async function runVersionsPin(
  client: IV2Client,
  id: string,
  versionId: string,
  pinned: boolean,
  json: boolean,
): Promise<void> {
  const resolved = resolveEntityId(id);
  const out = Output.fromOpts({ json, quiet: false });
  const action = pinned ? 'pin' : 'unpin';
  const version = await client.post<VersionRow>(
    `/api/v2/runtimes/${encodeURIComponent(resolved)}/versions/${encodeURIComponent(versionId)}/${action}`,
  );
  out.json(() => version);
  out.human(() =>
    pinned
      ? `Pinned ${version.version_id}. It is retained until you unpin it.`
      : `Unpinned ${version.version_id}. Normal retention applies.`,
  );
}

// ── Commander wiring ────────────────────────────────────────────────────────

export const versionsCommand: Command = addOutputOption(
  new Command('versions')
    .description('List the versions minted on each successful plug (and tag / pin / unpin them).')
    .argument('[id]', 'Entity slug or id (defaults to the entity linked in this directory)')
    .option('--limit <n>', 'Maximum number of versions to list', (v) => {
      const n = Number.parseInt(v, 10);
      if (!Number.isFinite(n) || n <= 0) throw new Error(`--limit must be a positive integer, got '${v}'`);
      return n;
    })
    .option('--json', 'Machine-readable JSON output (alias of -o json)'),
).action(async (id: string | undefined, opts: { limit?: number; json?: boolean; output?: string }) => {
  try {
    const client = await getV2Client();
    await runVersionsList({ client, id, limit: opts.limit, json: opts.json, output: opts.output });
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});

versionsCommand
  .command('tag <id> <version_id> <tag>')
  .description('Set a human label on a version (empty string clears it).')
  .option('--json', 'Machine-readable JSON output')
  .action(async (id: string, versionId: string, tag: string, opts: { json?: boolean }) => {
    try {
      const client = await getV2Client();
      await runVersionsTag(client, id, versionId, tag, !!opts.json);
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
    }
  });

versionsCommand
  .command('pin <id> <version_id>')
  .description('Pin a version so retention never reclaims its source.')
  .option('--json', 'Machine-readable JSON output')
  .action(async (id: string, versionId: string, opts: { json?: boolean }) => {
    try {
      const client = await getV2Client();
      await runVersionsPin(client, id, versionId, true, !!opts.json);
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
    }
  });

versionsCommand
  .command('unpin <id> <version_id>')
  .description('Unpin a version (normal retention applies again).')
  .option('--json', 'Machine-readable JSON output')
  .action(async (id: string, versionId: string, opts: { json?: boolean }) => {
    try {
      const client = await getV2Client();
      await runVersionsPin(client, id, versionId, false, !!opts.json);
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
    }
  });
